import { DUEL_STAT_PROFILES, duelLevelSkill, type DuelLevel } from '../battle/aiSkill';
import type { ArenaDef, TilesetDef } from '../battle/types';
import type { CharacterDef } from '../characters/types';
import type { GameDefinition } from './definition';

/** Versioned measurements, never faction bonuses. Opponent order is canonical and mirrored. */
export interface ArenaCalibration {
  version: 1;
  arena: string;
  /**
   * The level both fighters fought at: each at the level's shared step, its own percentages there
   * and the level's arena skill. Absent, Expert, as every table before phase D was measured. Roll
   * and Card read a level's table for a fight between two sides at that level; the Arena and a
   * fight between two levels read Expert's (`calibrationTable`).
   */
  level?: DuelLevel;
  /** Each character's stamp: `characterCombatSignature`, or `levelCombatSignature` at a level. */
  characters: Record<string, string>;
  probabilities: Record<string, number>;
  samples: number;
  /** Measurement reference only, never the editable or level-specific runtime stats. */
  reference?: Record<string, CharacterDef>;
  /**
   * The odds of a fight the attacker starts wounded, which the neutral table cannot see: a whole
   * Banshee shrugs off a Knight, one with a third of its health gone dies to a few hits. By
   * ordered pair (`attacker>defender`), one probability per entry of `shares` (the attacker's
   * remaining share of its health, ascending, below 1) against a whole defender, `samples` fights
   * each. Between the shares, and up to the neutral table at 1, the odds are read on the log-odds
   * line.
   */
  wounded?: {
    shares: number[];
    samples: number;
    probabilities: Record<string, number[]>;
    /**
     * The same, with the defender wounded instead: by ordered pair, the attacker's odds against a
     * defender at each share of its health, the attacker whole. Without it the defender's health
     * keeps the strength share, which the gate of 2026-09-27 found ten points short against
     * wounded Dark defenders: a Valkyrie beats a Banshee at half health every time, where the
     * share said one fight in ten.
     */
    defender?: Record<string, number[]>;
  };
}

/** Copy before editing so small property changes adjust odds continuously around real evidence. */
export function withCardCalibrationReferences(game: GameDefinition): GameDefinition {
  const copy = structuredClone(game);
  for (const table of copy.arenaCalibration ?? []) {
    // The estimate for an edited character is anchored on Expert's evidence; a level's table
    // simply stops standing for it, and the fight falls back on Expert's.
    if ((table.level ?? 'expert') !== 'expert') continue;
    const reference = { ...table.reference };
    for (const c of copy.characters) {
      if (!reference[c.id] && signatureStands(c, table.characters[c.id], true)) {
        const { model: _model, voice: _voice, ...combat } = c;
        reference[c.id] = structuredClone(combat);
      }
    }
    if (Object.keys(reference).length) table.reference = reference;
  }
  return copy;
}

/** A short, stable fingerprint of plain data: the same data gives the same text everywhere. */
export function fingerprint(value: unknown): string {
  const text = JSON.stringify(value);
  let a = 2166136261,
    b = 5381;
  for (let i = 0; i < text.length; i++) {
    a = Math.imul(a ^ text.charCodeAt(i), 16777619);
    b = Math.imul(b, 33) ^ text.charCodeAt(i);
  }
  return `${(a >>> 0).toString(16)}-${(b >>> 0).toString(16)}`;
}

const characters = new WeakMap<CharacterDef, string>();
const legacy = new WeakMap<CharacterDef, string>();
const arenas = new WeakMap<ArenaDef, WeakMap<TilesetDef, string>>();

/**
 * A character's combat as the measured duels fought it: between Experts, so without the factors
 * of the other levels. They were signed too, and nudging a Novice's reaction threw the measured
 * Expert odds away for the analytic model (content:bugs#3).
 */
function expertCombat(c: CharacterDef): CharacterDef['combat'] {
  if (!c.combat) return undefined;
  const { levels: _levels, aiLevels: _aiLevels, ...combat } = c.combat;
  return Object.keys(combat).length > 0 ? combat : undefined;
}

/** What a calibration table stamps a character with: what an Expert duel of it depends on. */
export function characterCombatSignature(c: CharacterDef, fresh = false): string {
  let value = fresh ? undefined : characters.get(c);
  if (!value) {
    value = fingerprint([
      // A shapeshifter's duel became a true copy of its opponent (D-56): its odds changed with the
      // rule, not its data, so a table measured before cannot stand for it.
      c.attack.type === 'mimic' ? 'expert-duel-v11-true-copy' : 'expert-duel-v11',
      c.tier,
      c.hitPoints,
      c.attack,
      expertCombat(c),
      c.movement.type,
      c.summoned,
      c.force,
      c.spellcaster,
    ]);
    characters.set(c, value);
  }
  return value;
}

/** The stamp tables were given before version 11, every level's factors in it. */
function legacyCombatSignature(c: CharacterDef): string {
  let value = legacy.get(c);
  if (!value) {
    value = fingerprint([
      'ai-fireball-steering-stun-aim-v10',
      c.tier,
      c.hitPoints,
      c.attack,
      c.combat,
      c.movement.type,
      c.summoned,
      c.force,
      c.spellcaster,
    ]);
    legacy.set(c, value);
  }
  return value;
}

/**
 * Whether a table's stamp still stands for a character: its signature now, or the one a table
 * saved before version 11 was stamped with, so that a game kept or published then keeps its
 * measured odds for as long as its characters are as they were measured.
 */
export function signatureStands(
  c: CharacterDef,
  stamp: string | undefined,
  fresh = false,
): boolean {
  return (
    stamp !== undefined &&
    (stamp === characterCombatSignature(c, fresh) ||
      (c.attack.type !== 'mimic' && stamp === legacyCombatSignature(c)))
  );
}

const levelSignatures = new WeakMap<CharacterDef, Map<DuelLevel, string>>();

/**
 * What a table measured at `level` stamps a character with: what a duel of it at that level
 * depends on. Its Expert duel, the level's shared step and arena skill, and its own percentages
 * and AI dials there; a change to any of them, in the game or in the ladder, and the table no
 * longer stands for it. At Expert, the Expert signature itself.
 */
export function levelCombatSignature(c: CharacterDef, level: DuelLevel, fresh = false): string {
  if (level === 'expert') return characterCombatSignature(c, fresh);
  let cache = levelSignatures.get(c);
  const cached = fresh ? undefined : cache?.get(level);
  if (cached) return cached;
  const { powerGoal: _powerGoal, ...step } = DUEL_STAT_PROFILES[level];
  const value = fingerprint([
    'duel-level-v1',
    level,
    characterCombatSignature(c, fresh),
    step,
    duelLevelSkill(level),
    c.combat?.levels?.[level] ?? null,
    c.combat?.aiLevels?.[level] ?? null,
  ]);
  if (!cache) levelSignatures.set(c, (cache = new Map()));
  cache.set(level, value);
  return value;
}

/** Whether a table's stamp at `level` still stands for a character. */
export function levelSignatureStands(
  c: CharacterDef,
  stamp: string | undefined,
  level: DuelLevel,
  fresh = false,
): boolean {
  return level === 'expert'
    ? signatureStands(c, stamp, fresh)
    : stamp !== undefined && stamp === levelCombatSignature(c, level, fresh);
}

/** A game's table for an arena (by `arenaCombatSignature`) at a level: Expert's when omitted. */
export function calibrationTable(
  game: Pick<GameDefinition, 'arenaCalibration'>,
  arena: string,
  level: DuelLevel = 'expert',
): ArenaCalibration | undefined {
  return game.arenaCalibration?.find((t) => t.arena === arena && (t.level ?? 'expert') === level);
}

export function arenaCombatSignature(a: ArenaDef, t: TilesetDef, fresh = false): string {
  let cache = arenas.get(a);
  if (!cache) {
    cache = new WeakMap();
    arenas.set(a, cache);
  }
  const cached = cache.get(t);
  if (cached && !fresh) return cached;
  // Names/IDs are editor labels; all simulation geometry must still match. Of the tileset only
  // the families the arena's map is made of count: one it never lays down fights in no duel, so
  // changing it (the lava of audit step 26c, in an arena with none) leaves the measured odds true.
  const { id: _id, name: _name, ...arena } = a;
  const laid = new Set([...a.rows.join('')].map((cell) => a.legend[cell]));
  // How the editor paints a tile is no part of a duel (editor:quality#16).
  const tiles = t.tiles
    .filter((tile) => laid.has(tile.id))
    .map(({ editor: _editor, ...tile }) => tile);
  const value = fingerprint([arena, tiles]);
  cache.set(t, value);
  return value;
}
